import { ContentBlock } from '../../../../../../../../../../../backend/src/types/types';

export const REF_PLACEHOLDER_TAG = 'kimref';
export const ARTIFACT_PLACEHOLDER_TAG = 'kimartifact';

const TABLE_SEPARATOR = /^\s*\|?[\s:|-]+\|?\s*$/;

function countTableCells(line: string): number {
  const trimmed = line.trim();
  if (!trimmed.startsWith('|')) return 0;
  const cells = trimmed.split('|').slice(1);
  if (trimmed.endsWith('|')) cells.pop();
  return cells.length;
}

// True when the accumulated markdown ends on a complete, closed table row (every column filled).
// A reference at that position has no cell to occupy, so it belongs after the table, not in it.
// Derived from the full text — not a single block — because streaming splits one table across many
// text blocks, so the final block alone is an unreliable fragment.
function endsOnClosedTableRow(markdown: string): boolean {
  const lines = markdown.split('\n').filter((line) => line.trim().length > 0);
  if (lines.length === 0) return false;

  let columns: number | null = null;
  for (const line of lines) {
    if (line.includes('-') && TABLE_SEPARATOR.test(line)) columns = countTableCells(line);
  }
  if (columns === null) return false;

  const lastLine = lines[lines.length - 1].trim();
  return lastLine.startsWith('|') && lastLine.endsWith('|') && countTableCells(lastLine) >= columns;
}

export function formatContent(blocks: ContentBlock[]): string {
  let result = '';
  // The pure markdown emitted so far (text blocks only, no placeholder tags). Used to decide
  // whether a reference sits after a finished table row, independent of how streaming chunked it.
  let markdown = '';
  let refsBreakOut = false;
  let prevWasRef = false;
  let prevWasText = false;

  for (const block of blocks) {
    if (block.type === 'text') {
      // Only break before a row when a placeholder (reference/artifact) interrupted the markdown.
      // Two consecutive text blocks are one continuous model stream whose own newlines already
      // separate rows, so a leading '|' there is a mid-row cell separator, not a new row.
      const needsBreak = !prevWasText && block.value.startsWith('|') && result.length > 0 && !result.endsWith('\n');
      result += needsBreak ? `\n${block.value}` : block.value;
      markdown += block.value;
      prevWasRef = false;
      prevWasText = true;
    } else if (block.type === 'artifact') {
      result += `<${ARTIFACT_PLACEHOLDER_TAG} data-id="${block.id}-${block.versionNumber}"></${ARTIFACT_PLACEHOLDER_TAG}>`;
      markdown = '';
      refsBreakOut = false;
      prevWasRef = false;
      prevWasText = false;
    } else if (block.type === 'reference') {
      // References that follow a full table row (no empty cell to fill) belong after the table, not in it.
      if (!prevWasRef) refsBreakOut = endsOnClosedTableRow(markdown);
      if (refsBreakOut && !prevWasRef) {
        result += '\n\n';
        // The table is finished; later references must judge themselves against fresh content.
        markdown = '';
      }
      result += `<${REF_PLACEHOLDER_TAG} data-id="${block.id}" data-ref-type="${block.refType}"></${REF_PLACEHOLDER_TAG}>`;
      prevWasRef = true;
      prevWasText = false;
    }
    // Survey blocks are rendered separately near the input, not inline.
  }
  return result;
}
